import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PhotoPlaceholder from "@/components/PhotoPlaceholder/PhotoPlaceholder";
import BookingResponse from "@/model/BookingResponse";
import isoDate from "@/utils/isoDate";
import { formatMoney } from "@/utils/pricing";
import guestsUtils from "@/utils/guests";
import "@/components/TripCard/TripCard.scss";

interface TripCardProps {
  booking: BookingResponse;
  highlighted?: boolean;
  /** Omit when the trip can no longer be cancelled. */
  onCancel?: () => void;
  isCancelling?: boolean;
}

function TripCard({
  booking,
  highlighted = false,
  onCancel,
  isCancelling = false,
}: TripCardProps) {
  const { t, i18n } = useTranslation();
  const cancelled = booking.status === "CANCELLED";
  const refunded = booking.refundedAmount ?? 0;
  const awaitingPayment =
    booking.status === "PENDING_PAYMENT" &&
    Boolean(booking.holdExpiresAt) &&
    new Date(booking.holdExpiresAt as string).getTime() > Date.now();

  return (
    <article
      className={[
        "trip-card",
        highlighted && "trip-card--highlighted",
        cancelled && "trip-card--cancelled",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="trip-card__image">
        {booking.propertyImageUrl ? (
          <img src={booking.propertyImageUrl} alt="" />
        ) : (
          <PhotoPlaceholder />
        )}
      </div>

      <div className="trip-card__body">
        <div className="trip-card__heading">
          <h3>
            {booking.propertyId ? (
              <Link to={`/properties/${booking.propertyId}`}>
                {booking.propertyTitle}
              </Link>
            ) : (
              booking.propertyTitle
            )}
          </h3>
          <span
            className={`trip-card__status trip-card__status--${booking.status?.toLowerCase()}`}
          >
            {t(`trips.status.${booking.status}`)}
          </span>
        </div>
        <p className="trip-card__muted">
          {[booking.propertyCity, booking.propertyCountry]
            .filter(Boolean)
            .join(", ")}
        </p>
        <p>
          {isoDate.formatIsoDate(booking.checkIn, i18n.language)} →{" "}
          {isoDate.formatIsoDate(booking.checkOut, i18n.language)} ·{" "}
          {t("trips.nights", { count: booking.nights ?? 0 })} ·{" "}
          {guestsUtils.describe(t, guestsUtils.ofBooking(booking))}
        </p>
        {booking.extras && booking.extras.length > 0 && (
          <p className="trip-card__muted">
            {t("trips.extras", {
              list: booking.extras.map((extra) => extra.name).join(", "),
            })}
          </p>
        )}
        {booking.status === "AWAITING_HOST" && booking.requestExpiresAt && (
          <p className="trip-card__request">
            {t("trips.awaitingHost", {
              deadline: new Date(booking.requestExpiresAt).toLocaleString(i18n.language, {
                weekday: "short",
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              }),
            })}
          </p>
        )}
        {booking.status === "DECLINED" && (
          <p className="trip-card__muted">
            {t(`trips.declineReason.${booking.declineReason ?? "HOST_DECLINED"}`)}
            {booking.declineMessage && (
              <>
                {" "}
                <q>{booking.declineMessage}</q>
              </>
            )}
          </p>
        )}
        {refunded > 0 && (
          <p className="trip-card__muted">
            {t("trips.refunded", {
              amount: formatMoney(refunded, booking.currency, i18n.language),
            })}
          </p>
        )}
        <div className="trip-card__footer">
          <strong>
            {formatMoney(booking.totalAmount, booking.currency, i18n.language)}
          </strong>
          <div className="trip-card__actions">
            {booking.canReview && (
              <Link
                className="trip-card__pay"
                to={`/trips/${booking.id}/review`}
              >
                {t("trips.leaveReview")}
              </Link>
            )}
            {booking.reviewId && (
              <Link to={`/trips/${booking.id}/review`}>
                {t("trips.yourReview")}
              </Link>
            )}
            {awaitingPayment && (
              <Link className="trip-card__pay" to={`/trips/${booking.id}/pay`}>
                {t("trips.completePayment")}
              </Link>
            )}
            {booking.receiptUrl && (
              <a href={booking.receiptUrl} target="_blank" rel="noreferrer">
                {t("trips.receipt")}
              </a>
            )}
            {onCancel && (
              <button type="button" onClick={onCancel} disabled={isCancelling}>
                {isCancelling ? t("trips.cancelling") : t("trips.cancelButton")}
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default TripCard;
export type { TripCardProps };
